import React from 'react'
import PropTypes from 'prop-types'
import Button from 'portal-modules/common/components/Button'
import RpModal from 'portal-modules/common/components/modals/rp-modal'
import styled from '@emotion/styled'
import { connect } from 'react-redux'

const StyledWrapperUL = styled('ul')`
  color: ${({ theme }) => theme.text.dark};
  list-style-type: none;
  margin: 0;
  padding: 0;
`

const StyledListItem = styled('li')`
  display: flex;
  justify-content: space-between;
  padding-bottom: 8px;
  font-size: 14px;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.gray.dark};

  :last-child {
    padding: 8px 0 0 0;
  }
`

const StyledButton = styled(Button)`
  min-width: 100%;
  font-size: 18px;
  border-radius: 30px;
`

const StyledChargesDiv = styled('div')`
  padding: 16px;
`

const StyledContentHeaderSpan = styled('span')`
  font-weight: bold;
`

const StyledTermName = styled('em')`
  font-size: 12px;
  margin: 7px 0;
`

const StyledSpan = styled('span')`
  flex-grow: 1;
  overflow: hidden;
  position: relative;
  font-size: 13px;

  &:after {
    width: 100%;
    border-bottom: 1px dashed ${({ theme }) => theme.gray.light};
    bottom: 4px;
    content: '';
    position: absolute;
  }
`

const StyledAmountSpan = styled('span')`
  font-size: 13px;
  margin-top: auto;
`

const formattedOfferData = charges =>
  charges
    ? Object.keys(charges).reduce((arr, key) => {
        return [
          ...arr,
          {
            ...charges[key],
            other_charge_id: key,
          },
        ]
      }, [])
    : []

const installmentChargesModal = ({
  id,
  charges,
  concessions,
  frequency,
  taxLabel,
  rent,
  tax_amount,
  total,
  due_date,
  installment_term,
  installment_name,
  includeTaxesInAdvertisedPricing,
  isLeaseAndRentWordReplaced,
  ...rest
}) => {
  const chargeData = formattedOfferData(charges)
  const concessionData = formattedOfferData(concessions)
  const rentReplacedText = isLeaseAndRentWordReplaced
    ? __('Housing Fee')
    : __('Rent')

  const isNoInstallment = 'no_installment' == id
  return (
    <RpModal
      {...rest}
      title={
        isNoInstallment
          ? __('Recurring Charges Details')
          : __('Installment Details')
      }
      footerContent={
        <StyledButton slim appearance='primary' onClick={rest.onClose}>
          {__('Close')}
        </StyledButton>
      }
      noPad
    >
      <StyledWrapperUL>
        <StyledChargesDiv>
          <StyledListItem>
            <StyledContentHeaderSpan>
              {isNoInstallment
                ? __('{%s,0} Recurring Charges', [frequency])
                : installment_name}
              {due_date ? __(' - {%s,0}', [due_date]) : ''}
            </StyledContentHeaderSpan>
          </StyledListItem>
          <StyledListItem>
            <StyledTermName>
              {!isNoInstallment &&
                __('Installment Term - {%s,0}', [installment_term])}
            </StyledTermName>
          </StyledListItem>
          {isNoInstallment && (
            <StyledListItem>
              <StyledSpan>{rentReplacedText}</StyledSpan>
              <StyledAmountSpan>
                {i18nUtils.currency(rent || 0, 'p:2')}
              </StyledAmountSpan>
            </StyledListItem>
          )}

          {chargeData &&
            chargeData.map(charge => (
              <StyledListItem key={charge.charge_name}>
                <StyledSpan>{charge.charge_name}</StyledSpan>
                <StyledAmountSpan>
                  {i18nUtils.currency(charge.new_charge_amount || 0, 'p:2')}
                </StyledAmountSpan>
              </StyledListItem>
            ))}
          {concessionData &&
            concessionData.map(({ charge_name, new_charge_amount }) => (
              <StyledListItem key={charge_name}>
                <StyledSpan>{charge_name}</StyledSpan>
                <StyledAmountSpan>
                  {i18nUtils.currency(new_charge_amount || 0, 'p:2')}
                </StyledAmountSpan>
              </StyledListItem>
            ))}

          {!includeTaxesInAdvertisedPricing && !!tax_amount && (
            <StyledListItem>
              <StyledSpan>{__('Taxes')}</StyledSpan>
              <StyledAmountSpan>
                {i18nUtils.currency(tax_amount, 'p:2')}
              </StyledAmountSpan>
            </StyledListItem>
          )}
          <StyledListItem>
            <StyledSpan>
              <b>{__('Total')}</b>
            </StyledSpan>
            <StyledAmountSpan>
              <b>{i18nUtils.currency(total, 'p:2')}</b>
            </StyledAmountSpan>
          </StyledListItem>
        </StyledChargesDiv>
      </StyledWrapperUL>
    </RpModal>
  )
}

installmentChargesModal.propTypes = {
  id: PropTypes.any,
  taxLabel: PropTypes.string,
  charges: PropTypes.object,
  rateFrequency: PropTypes.string,
  rent: PropTypes.number,
  tax_amount: PropTypes.number,
  total: PropTypes.number,
  due_date: PropTypes.string,
  installment_term: PropTypes.string,
  installment_name: PropTypes.string,
  includeTaxesInAdvertisedPricing: PropTypes.bool,
  isLeaseAndRentWordReplaced: PropTypes.bool,
}

installmentChargesModal.defaultProps = {
  charges: {},
  concessions: {},
  due_date: '',
  installment_term: '',
}

export default connect(({ settings: { renewal }, productPermissions }) => ({
  taxLabel: (renewal && renewal.tax_label) || '',
  includeTaxesInAdvertisedPricing:
    renewal && renewal.include_taxes_in_advertised_pricing,
  isLeaseAndRentWordReplaced:
    productPermissions?.replace_lease_and_rent || false,
}))(installmentChargesModal)
